import React, { Component } from 'react'
import * as d3 from 'd3'
import {
  getMatrixAxisLabel,
  collapseOrExpandNode,
  collapseAll,
  removeAllParents,
  strokeWidth,
  expandNode,
  collapseNode,
  getFontSize,
  getGroupColors,
  getSize
} from 'lib/helper'
import { filterGraphData, waitForFontLoad } from 'lib/helper'


export default class Matrix extends Component {
  constructor(props) {
    super(props)
    this.matrixRef = React.createRef()
    this.scrollRef = React.createRef()
  }

  getSize = (node) => {
    return this.defaultSize * getSize(node)
  }

  setSelection = (i, rowOrCol, force) => {
    let currentDiv = this.matrixRef.current
    let u
    if (rowOrCol === 'row')
      u = d3.select(currentDiv).selectAll('.matrixrow').nodes()
    else
      u = d3.select(currentDiv).selectAll('.column').nodes()
    const ele = d3.select(u[i])
    let selected
    if (force === undefined)
      selected = !ele.classed('selection')
    else
      selected = force
    d3.select(u[i]).classed('selection', selected)
    ele.selectAll('.selection').remove()

    if (selected) {
      ele.insert('rect', ':first-child')
        .attr('class', 'selection')
        .attr('fill-opacity', '0.1')
        .attr('width', this.svgWidth)
        .attr('height', this.nodes[i].size)
        .attr('y', 0)
        .attr('x', (rowOrCol === 'row' ? -this.text_margin : -this.width))
    }
    return this.nodes[i][rowOrCol + 'selection'] = selected
  }

  onClickAxis = (ax, rowOrCol) => {
    if (this.props.notCollapse) {
      return null;
    }
    return (p, i) => {
      let clickedNode = this.nodes[i]
      if (collapseOrExpandNode(clickedNode, ax, this.totalNodes)) {
        this.drawMatrix()
      } else {
        this.setSelection(i, rowOrCol)
      }
    }
  }

  addAxis = (addAxises) => {
    let axises = this.props.matrixFilter.axis
    let axisTexts = this.props.matrixFilter.axisText
    let newAxis = axises
    let newAxisText = axisTexts
    addAxises.forEach(([axis, axisText]) => {
      if (axises.indexOf(axis) === -1) {
        newAxis = [axis, ...newAxis]
        newAxisText = [axisText, ...newAxisText]
      }
    })
    this.props.setMatrixFilter({
      ...this.props.matrixFilter,
      axis: newAxis,
      axisText: newAxisText
    })

  }

  axisHeaderClick = ax => {
    if (this.props.notCollapse) {
      return null;
    }
    return () => {
      if (ax === 'name')
        return
      if (this.collapseAllBy === ax) {
        this.collapseAllBy = null
        removeAllParents(this.totalNodes)
        this.drawMatrix()
      } else {
        this.collapseAllBy = ax
        removeAllParents(this.totalNodes)
        collapseAll(ax, this.nodes)
        let { axis, axisText } = this.props.matrixFilter
        let idx = axis.indexOf('name')
        if (idx > -1) {
          axis.splice(idx, 1)
          axisText.splice(idx, 1)
          this.props.setMatrixFilter({
            ...this.props.matrixFilter,
            axis: [...axis],
            axisText: axisText
          })
        } else {
          this.drawMatrix()
        }
      }
    }
  }

  onclickCell = (d, i) => {
    if (this.nodes[d.x].ax || this.nodes[d.y].ax) {
      expandNode(this.nodes[d.x])
      expandNode(this.nodes[d.y])
      this.drawMatrix()
    } else {
      let { is_superuser } = this.props.profile
      // is_superuser = true
      if (is_superuser) {
        this.props.showEditModal(this.nodes[d.x], this.nodes[d.y], d.link)
      } else if (this.collapseAllBy) {
        collapseNode(this.nodes[d.x], this.collapseAllBy, this.totalNodes)
        collapseNode(this.nodes[d.y], this.collapseAllBy, this.totalNodes)
        this.drawMatrix()
      }
    }
  }

  getMargins = (nodes) => {
    let firstColumn = true;
    return this.axis.map((ax, i) => {
      let margin = d3.max(nodes, node => {
        let f = this.textFontSize + 'px Open Sans'
        let element = document.createElement('canvas')
        let context = element.getContext('2d')
        context.font = f;
        let text = '';
        if (!this.collapseAllBy || this.collapseAllBy === ax) {
          text = getMatrixAxisLabel(node, ax);
        } else {
          if (this.collapseAllBy === 'department' && ax === 'division') {
            text = getMatrixAxisLabel(node, ax);
          } else {
            text = '';
          }
        }
        return context.measureText(text).width
      })
      if (margin > 0) {
        let f = this.textFontSize + 'px Open Sans'
        let element = document.createElement('canvas')
        let context = element.getContext('2d')
        context.font = f
        let axisTextWidth = context.measureText(this.axisText[i]).width + this.textFontSize * 2
        if (margin < axisTextWidth) {
          margin = axisTextWidth
        }
        if (firstColumn) {
          margin += this.textFontSize
          firstColumn = false
        }
        margin += 10
      }
      return margin
    })
  }

  drawMatrix = (first = false) => {
    const { matrixFilter, data: initData } = this.props
    let { fileter_role: roles, is_base: isBase } = initData;
    roles = roles || []
    const colorOptions = this.props.colorOptions
    this.colorOptions = colorOptions;
    let { nodes: totalNodes } = this.props.data.result
    totalNodes.forEach(node => {
      node.count = 0
      node.sourceRaw = []
    })

    if (first && !this.props.notCollapse) {
      let ax = 'groupName'

      if (isBase) {
        ax = 'division'
        if (totalNodes.every(node => getMatrixAxisLabel(node, ax) === ''))
          ax = 'department'
      }
      this.collapseAllBy = ax
      totalNodes.forEach(node => node.filtered = true)
      collapseAll(ax, totalNodes)
    }

    let { axis, axisText } = matrixFilter

    if (axis.indexOf('name') > -1) {
      this.collapseAllBy = null;
      removeAllParents(totalNodes);
    }

    const data = filterGraphData(this.props.data.result, matrixFilter || { filterActivities: roles }, this.collapseAllBy)

    let { nodes, matrix, filteredNodes, minPercent, maxPercent } = data

    let n = nodes.length
    let width = this.props.width || 1200
    let height = this.props.height || 1200
    this.totalNodes = filteredNodes

    let textFontSize = Math.max(Math.min(width / this.totalNodes.length - 2, 20), 10)
    let defaultSize = textFontSize + 2
    this.defaultSize = defaultSize
    let firstColumn = true
    this.axis = axis;
    this.axisText = axisText;
    this.textFontSize = textFontSize;
    let margins = this.getMargins(nodes);

    let text_margin = d3.sum(margins)
    let margin = { top: text_margin, right: 10, bottom: 10, left: text_margin }
    this.text_margin = text_margin

    let that = this

    // Compute index per node.
    this.nodes = nodes
    this.matrix = matrix

    nodes.forEach(node => {
      node.size = this.defaultSize * getSize(node)
    })

    this.order = this.getOrder(matrixFilter.order)

    let sz = this.order.map(id => nodes[id].size)
    let x = new Array(n)
    this.x = x
    for (let i = 0; i < n; i++) {
      x[this.order[i]] = d3.sum(sz.slice(0, i))
    }

    width = d3.sum(sz)
    height = d3.sum(sz)

    let svgWidth = width + margin.left + margin.right
    let svgHeight = height + margin.top + margin.bottom

    this.svgWidth = svgWidth
    this.width = width

    this.startPos = null;


    let svg = d3
      .select(this.matrixRef.current)
      .html('')
      .append('svg')
      .attr('viewBox', `0 0 ${svgWidth - 5} ${svgHeight - 5}`)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')
      .on("mousedown", function () {
        if (that.props.notCollapse) {
          return;
        }
        that.startPos = d3.mouse(this)
      })
      .on("mousemove", function () {
        if (that.startPos) {
          let [sx, sy] = that.startPos
          let [ex, ey] = d3.mouse(this)
          if (sx > ex) {
            let tmp = sx;
            sx = ex;
            ex = tmp
          }
          if (sy > ey) {
            let tmp = sy;
            sy = ey;
            ey = tmp
          }

          if (ex - sx < 20 && ey - sy < 20) // threshold
            return
          for (let i = 0; i < that.x.length; i++) {
            if (sx >= 0 && ex >= 0) {
              if (that.x[i] + that.nodes[i].size >= sx && that.x[i] < ex) {
                that.setSelection(i, 'col', true)
              } else {
                that.setSelection(i, 'col', false)
              }

            }
            if (sy >= 0 && ey >= 0) {
              if (that.x[i] + that.nodes[i].size >= sy && that.x[i] < ey) {
                that.setSelection(i, 'row', true)
              } else {
                that.setSelection(i, 'row', false)
              }
            }
          }
        }
      }).on('mouseup', function () {
        that.startPos = null
      })

    this.svg = svg


    let z = d3.scaleLinear().domain([0, 4])

    svg
      .append('rect')
      .attr('class', 'matrix-corner')
      .attr('width', text_margin - defaultSize)
      .attr('height', text_margin - defaultSize)
      .attr('x', -text_margin)
      .attr('y', -text_margin)


    svg
      .append('rect')
      .attr('class', 'background')
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .attr('x', -text_margin)
      .attr('y', -text_margin)
      .attr('fill', 'white')
      .on('mousemove', mousemovebackground)
      .on('click', mouseclickbackground)

    function getmousepositiononbackground(elem) {
      const [mx, my] = d3.mouse(elem)

      let px, py
      for (let i = 0; i < x.length; i++) {
        if (mx < that.x[i] + nodes[i].size && mx > that.x[i]) {
          px = i;
          break
        }
      }
      for (let i = 0; i < x.length; i++) {
        if (my < that.x[i] + nodes[i].size && my > that.x[i]) {
          py = i;
          break
        }
      }
      return { px, py }
    }

    function mouseclickbackground(d) {
      const { px, py } = getmousepositiononbackground(this)
      if (px !== undefined && py !== undefined) {
        that.onclickCell({ x: px, y: py })
      }
    }

    function mousemovebackground(d) {
      const { px, py } = getmousepositiononbackground(this)
      mouseleave()
      if (px !== undefined && py !== undefined) {
        mouseover({ x: px, y: py })
      }
    }



    firstColumn = true

    axis.forEach((ax, i) => {
      svg.append('line')
        .attr('x1', -d3.sum(margins.slice(0, i)))
        .attr('x2', -d3.sum(margins.slice(0, i)))
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', '#e7e9ee')
      svg.append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', -d3.sum(margins.slice(0, i)))
        .attr('y2', -d3.sum(margins.slice(0, i)))
        .attr('stroke', '#e7e9ee')
      if (margins[i] > 0) {
        let firstMargin = firstColumn ? textFontSize : 3
        firstColumn = false
        svg.append('text')
          .attr('x', -firstMargin - d3.sum(margins.slice(0, i)))
          .attr('y', 0)
          .attr('dy', '-0.3em')
          .attr('font-family', 'Open Sans')
          .attr('font-weight', 'bold')
          .attr('text-anchor', 'end')
          .attr('font-size', defaultSize - 2)
          .attr('fill', '#A033FF')
          .text(axisText[i])
          .on('click', that.axisHeaderClick(ax))
        svg.append('text')
          .attr('x', firstMargin + d3.sum(margins.slice(0, i)))
          .attr('y', 0)
          .attr('dy', '-0.3em')
          .attr('font-family', 'Open Sans')
          .attr('font-weight', 'bold')
          .attr('text-anchor', 'start')
          .attr('font-size', defaultSize - 2)
          .attr('fill', '#A033FF')
          .attr('transform', 'rotate(-90)')
          .text(axisText[i])
          .on('click', that.axisHeaderClick(ax))
      }
    })

    let column = svg
      .selectAll('.column')
      .data(matrix)
      .enter()
      .append('g')
      .attr('class', 'column')
      .classed('selection', (d, i) => that.nodes[i].colselection)
      .attr('transform', (d, i) => {
        return 'translate(' + x[i] + ')rotate(-90)'
      })

    column.append('line').attr('x1', -height).attr('x2', text_margin).attr('stroke', '#e7e9ee')

    firstColumn = true
    axis.forEach((ax, i) => {
      if (margins[i] > 0) {
        let firstMargin = firstColumn ? textFontSize : 3
        firstColumn = false
        column.append('rect')
          .attr('x', d3.sum(margins.slice(0, i)))
          .attr('y', 1)
          .attr('height', (d, i) => nodes[i].size - 2)
          .attr('width', margins[i])
          .attr('class', 'axis-rect')
          .attr('fill-opacity', 0)
          .on('click', that.onClickAxis(ax, 'col'))
          .on('mousemove', function (d, i) {
            let that = this
            mousemoveaxis(that, nodes[i])
          })
          .on('mouseleave', mouseleave)
        column
          .append('text')
          .attr('x', d3.sum(margins.slice(0, i)) + firstMargin)
          .attr('y', (d, i) => (nodes[i].size + defaultSize) / 2 - 1)
          .attr('class', 'axis-text')
          .attr('dy', '-0.3em')
          .attr('text-anchor', 'start')
          .attr('font-family', 'Open Sans')
          .attr('font-size', defaultSize - 2)
          .text((d, i) => {
            return getMatrixAxisLabel(nodes[i], ax)
          })
          .on('click', that.onClickAxis(ax, 'col'))
          .on('mousemove', function (d, i) {
            let that = this
            mousemoveaxis(that, nodes[i])
          })
          .on('mouseleave', mouseleave)
      }
    })

    const rows = svg
      .selectAll('.matrixrow')
      .data(matrix)
      .enter()
      .append('g')
      .attr('class', 'matrixrow')
      .classed('selection', (d, i) => that.nodes[i].rowselection)
      .attr('transform', (d, i) => {
        return 'translate(0,' + x[i] + ')'
      })
      .each(row)
    rows.append('line').attr('x2', width).attr('x1', -text_margin).attr('stroke', '#e7e9ee')

    firstColumn = true
    axis.forEach((ax, i) => {
      if (margins[i] > 0) {
        let firstMargin = firstColumn ? textFontSize : 3
        firstColumn = false
        rows.append('rect')
          .attr('x', -d3.sum(margins.slice(0, i + 1)))
          .attr('y', 1)
          .attr('height', (d, i) => nodes[i].size - 2)
          .attr('width', margins[i])
          .attr('class', 'axis-rect')
          .attr('fill-opacity', 0)
          .on('click', that.onClickAxis(ax, 'row'))
          .on('mousemove', function (d, i) {
            let that = this
            mousemoveaxis(that, nodes[i])
          })
          .on('mouseleave', mouseleave)
        rows
          .append('text')
          .attr('x', -d3.sum(margins.slice(0, i)) - firstMargin)
          .attr('y', (d, i) => (nodes[i].size + defaultSize) / 2 - 1)
          .attr('class', 'axis-text')
          .attr('dy', '-0.3em')
          .attr('text-anchor', 'end')
          .attr('font-family', 'Open Sans')
          .attr('font-size', defaultSize - 2)
          .text((d, i) => {
            return getMatrixAxisLabel(nodes[i], ax)
          })
          .on('click', that.onClickAxis(ax, 'row'))
          .on('mousemove', function (d, i) {
            let that = this
            mousemoveaxis(that, nodes[i])
          })
          .on('mouseleave', mouseleave)
      }
    })

    let oppositeHighlight = svg.append('rect')
      .attr('class', 'opposite-highlight')
      .attr('width', 0)
      .attr('height', 0)
      .style('display', 'none')
    let hoverHighlight = svg.append('rect')
      .attr('class', 'hover-highlight')
      .attr('width', 0)
      .attr('height', 0)
      .style('display', 'none')
    function row(row) {
      let r = d3.select(this)
        .selectAll('.cell')
        .data(
          row.filter(d => {
            return that.props.notCollapse || d.z || d.border
          })
        )
        .enter()
      r.append('rect')
        .attr('class', 'cell')
        .attr('data-style', (d) => {
           if (nodes[d.x].group === nodes[d.y].group && d.count > 0) {
             return 'same'
           }
           if (nodes[d.x].group !== nodes[d.y].group && d.count > 0) {
            return 'different'
          }
        })
        .attr('x', d => {
          return x[d.x] + 1 + strokeWidth(d) / 2
        })
        .attr('y', d => 1 + strokeWidth(d) / 2)
        .attr('width', d => nodes[d.x].size - 2 - strokeWidth(d))
        .attr('height', d => nodes[d.y].size - 2 - strokeWidth(d))
        .style('fill-opacity', d => {
          if (d.x !== d.y && d.percent)
            return (d.percent - minPercent) / (maxPercent - minPercent) * 0.4 + 0.5;
          else
            return d.count ? z(d.z / d.count) : 0
        })
        .style('fill', d => {
          return nodes[d.x].group === nodes[d.y].group && colorOptions[nodes[d.x].group]
        })
        .style('stroke', d => {
          if (d.edited)
            return 'red'
          return nodes[d.x].group === nodes[d.y].group && colorOptions[nodes[d.x].group]
        })
        .style('stroke-width', d => {
          return strokeWidth(d)
        })
        .style('stroke-opacity', 1)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave)
        .on('click', that.onclickCell)
      let fontSize = defaultSize
      r.append('text')
        .filter(d => d.percent && (nodes[d.x].ax || nodes[d.y].ax))
        .attr('x', d => {
          return x[d.x] + nodes[d.x].size / 2
        })
        .attr('y', d => {
          return (nodes[d.y].size + getFontSize(fontSize, nodes, d)) / 2
        })
        .attr('dy', '-0.3em')
        .attr('text-anchor', 'middle')
        .attr('class', 'cell-text')
        .style('fill', 'white')
        .attr('font-size', d => getFontSize(fontSize, nodes, d) - 2)
        .text(d => d.percent.toFixed(0) + '%')
        .attr('transform', d => {
          if (nodes[d.x].size < defaultSize * 2 && nodes[d.x].size < nodes[d.y].size)
            return `rotate(-90, ${x[d.x] + nodes[d.x].size / 2 + 1}, ${(nodes[d.y].size) / 2})`
        })
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave)
        .on('click', that.onclickCell)
    }

    let currentDiv = this.matrixRef.current

    d3.select(currentDiv)
      .selectAll('.matrixrow.selection')
      .insert('rect', ':first-child')
      .attr('class', 'selection')
      .attr('fill-opacity', '0.1')
      .attr('width', width + text_margin)
      .attr('height', function (d, i) { return that.nodes[d[0].x].size })
      .attr('y', 0)
      .attr('x', -text_margin)
    d3.select(currentDiv)
      .selectAll('.column.selection')
      .insert('rect', ':first-child')
      .attr('class', 'selection')
      .attr('width', width + text_margin)
      .attr('height', function (d, i) { return that.nodes[d[0].x].size })
      .attr('fill-opacity', '0.1')
      .attr('y', 0)
      .attr('x', -width)

    function mouseover(p) {
      if (p.x !== p.y) {
        oppositeHighlight.style('display', 'block')
          .attr('x', that.x[p.y])
          .attr('y', that.x[p.x])
          .attr('width', nodes[p.y].size)
          .attr('height', nodes[p.x].size)
          .attr('stroke', (d, i) => {
            return that.nodes[p.x].group === that.nodes[p.y].group ? that.colorOptions[that.nodes[p.x].group] : 'black'
          })
          .attr('fill-opacity', 0)
          .attr('stroke-width', 4)
      } else {
        oppositeHighlight.style('display', 'none')
      }
      hoverHighlight.style('display', 'block')
        .attr('x', that.x[p.x])
        .attr('y', that.x[p.y])
        .attr('width', nodes[p.x].size)
        .attr('height', nodes[p.y].size)
        .attr('stroke', (d, i) => {
          return that.nodes[p.x].group === that.nodes[p.y].group ? that.colorOptions[that.nodes[p.x].group] : 'black'
        })
        .attr('fill-opacity', 0)
        .attr('stroke-width', 4)
      d3.select(currentDiv)
        .selectAll('.matrixrow')
        .classed('active', (d, i) => {
          return (p.y === i)
        })
      d3.select(currentDiv)
        .selectAll('.column')
        .classed('active', (d, i) => {
          return (p.x === i)
        })
      d3.select(currentDiv)
        .selectAll('.matrixrow.active')
        .insert('rect', ':first-child')
        .attr('class', 'highlight')
        .attr('fill-opacity', '0.05')
        .attr('width', width + text_margin)
        .attr('height', nodes[p.y].size - 2)
        .attr('y', 1)
        .attr('x', -text_margin)
      d3.select(currentDiv)
        .selectAll('.column.active')
        .insert('rect', ':first-child')
        .attr('class', 'highlight')
        .attr('width', width + text_margin)
        .attr('height', nodes[p.x].size - 2)
        .attr('fill-opacity', '0.05')
        .attr('y', 1)
        .attr('x', -width)
    }

    function mouseleave(d) {
      d3.selectAll('.highlight').remove()
      d3.select(currentDiv)
        .selectAll('.column.active')
        .classed('active', false)
      d3.select(currentDiv)
        .selectAll('.matrixrow.active')
        .classed('active', false)
      oppositeHighlight.style('display', 'none')
      hoverHighlight.style('display', 'none')
    }

    function mousemove(d) {
      if (!d.z || that.props.notCollapse)
        return
     }

    function mousemoveaxis(element, node) {
      if (that.props.notCollapse) {
        return;
      }
    }
  }

  getOrder = options => {
    let nodes = this.nodes
    let n = nodes.length

    options = options || []

    return d3.range(n).sort((a, b) => {
      for (let i = 0; i < options.length; i++) {
        let { value } = options[i]
        let propA = nodes[a][value]
        let propB = nodes[b][value]
        try {
          if (propA !== propB) {
            if (typeof (propA) === 'number') {
              if (value === 'count')
                return propB - propA
              else
                return propA - propB
            } else {
              return propA.localeCompare(propB)
            }
          }
        } catch (e) {
        }
      }
      return 0
    })
  }


  orderByOption = value => {
    let nodes = this.nodes
    let n = nodes.length
    let defaultSize = this.defaultSize
    this.order = this.getOrder(value)
    let sz = this.order.map(id => nodes[id].size)
    let x = new Array(n)
    this.x = x
    for (let i = 0; i < n; i++) {
      x[this.order[i]] = d3.sum(sz.slice(0, i))
    }
    let svg = this.svg

    let t = svg.transition().duration(2500)
    let r = t.selectAll('.matrixrow')
      .delay((d, i) => {
        return x[i] / this.width * 1500
      })
      .attr('transform', (d, i) => {
        return 'translate(0,' + x[i] + ')'
      })

    r.selectAll('.cell')
      .delay((d) => {
        return x[d.x] / this.width * 1500
      })
      .attr('x', d => {
        return x[d.x] + 1 + strokeWidth(d) / 2
      })
    r.selectAll('.cell-text')
      .delay(d => {
        return x[d.x] / this.width * 1500
      })
      .attr('x', d => {
        return x[d.x] + nodes[d.x].size / 2
      })
      .attr('transform', d => {
        if (nodes[d.x].size < defaultSize * 2 && nodes[d.x].size < nodes[d.y].size)
          return `rotate(-90, ${x[d.x] + nodes[d.x].size / 2 + 1}, ${(nodes[d.y].size) / 2})`
      })
    t.selectAll('.column')
      .delay((d, i) => {
        return x[i] / this.width * 1500
      })
      .attr('transform', (d, i) => {
        return 'translate(' + x[i] + ')rotate(-90)'
      })
  }



  orderByNodes = (newNodes) => {
    let nodes = this.nodes
    let n = nodes.length
    let colorOptions = {};
    const c = getGroupColors();
    newNodes.forEach(({ group }) => {
      colorOptions[group] = '#' + c(group);
    });
    this.colorOptions = colorOptions;
    let defaultSize = this.defaultSize
    this.revOrder = d3.range(n);
    this.order = d3.range(n).map((i) => {
      for (let ni = 0; ni < n; ni++) {
        if (newNodes[i].id === nodes[ni].id) {
          nodes[ni].group = newNodes[i].group;
          this.revOrder[ni] = i;
          return ni
        }
      }
      return undefined
    });

    let sz = this.order.map(id => nodes[id].size)
    let x = new Array(n)
    this.x = x
    for (let i = 0; i < n; i++) {
      x[this.order[i]] = d3.sum(sz.slice(0, i))
    }
    let that = this;
    let svg = this.svg
    let t = svg.transition().duration(500)
    let r = t.selectAll('.matrixrow')
      .delay((d, i) => {
        return x[i] / this.width * 100
      })
      .attr('transform', (d, i) => {
        return 'translate(0,' + x[i] + ')'
      })
    r.selectAll('.axis-text')
      .text((d, i) => {
        let idx = that.revOrder[d[0].y]
        return getMatrixAxisLabel(newNodes[idx], this.axis[i])
      });

    r.selectAll('.cell')
      .delay((d) => {
        return x[d.x] / this.width * 100
      })
      .attr('x', d => {
        return x[d.x] + 1 + strokeWidth(d) / 2
      })
      .style('fill', d => {
        let idx = that.revOrder[d.x]
        let idy = that.revOrder[d.y]
        return newNodes[idx].group === newNodes[idy].group ? colorOptions[newNodes[idx].group] : 'black'
      })
      .style('stroke', d => {
        let idx = that.revOrder[d.x]
        let idy = that.revOrder[d.y]
        return newNodes[idx].group === newNodes[idy].group ? colorOptions[newNodes[idx].group] : 'transparent'
      })
    r.selectAll('.cell-text')
      .delay(d => {
        return x[d.x] / this.width * 100
      })
      .attr('x', d => {
        return x[d.x] + nodes[d.x].size / 2
      })
      .attr('transform', d => {
        if (nodes[d.x].size < defaultSize * 2 && nodes[d.x].size < nodes[d.y].size)
          return `rotate(-90, ${x[d.x] + nodes[d.x].size / 2 + 1}, ${(nodes[d.y].size) / 2})`
      })
    t.selectAll('.column')
      .delay((d, i) => {
        return x[i] / this.width * 100
      })
      .attr('transform', (d, i) => {
        return 'translate(' + x[i] + ')rotate(-90)'
      })
      .selectAll('.axis-text')
      .text((d, i) => {
        let idx = that.revOrder[d[0].y]
        return getMatrixAxisLabel(newNodes[idx], this.axis[i])
      });
  }

  highlightIndex = (ids) => {
    let currentDiv = this.matrixRef.current;
    d3.select(currentDiv)
      .selectAll('.matrixrow')
      .classed('selected', (d, i) => {
        return ids.indexOf(i) >= 0
      })
    d3.select(currentDiv)
      .selectAll('.column')
      .classed('selected', (d, i) => {
        return ids.indexOf(i) >= 0
      })
    d3.select(currentDiv)
      .selectAll('.matrixrow.selected')
      .insert('rect', ':first-child')
      .attr('class', 'select-highlight')
      .attr('fill-opacity', '0.1')
      .attr('width', this.width + this.text_margin)
      .attr('height', this.nodes[0].size - 2)
      .attr('y', 1)
      .attr('x', -this.text_margin)
    d3.select(currentDiv)
      .selectAll('.column.selected')
      .insert('rect', ':first-child')
      .attr('class', 'select-highlight')
      .attr('width', this.width + this.text_margin)
      .attr('height', this.nodes[0].size - 2)
      .attr('fill-opacity', '0.1')
      .attr('y', 1)
      .attr('x', -this.width)
  }

  highlightChoosen = (choosen) => {
    if (Array.isArray(choosen) && choosen.length === 0) {
      return;
    }
    let currentDiv = this.matrixRef.current;
    d3.selectAll('.select-highlight').remove()
    d3.select(currentDiv)
      .selectAll('.column.selected')
      .classed('selected', false)
    d3.select(currentDiv)
      .selectAll('.matrixrow.selected')
      .classed('selected', false)
    if (!choosen) {
      return;
    }

    if (Array.isArray(choosen)) {
      this.highlightIndex(choosen.map(c => this.nodes.findIndex(node => node.id === c.id)).filter(id => id > -1));
    } else {
      const id = this.nodes.findIndex(node => node.id === choosen.id);
      if (id === -1) {
        return;
      }
      this.highlightIndex([id,]);
    }
  }

  componentDidMount = () => {
    waitForFontLoad('10px Open Sans').then(() => this.drawMatrix(true));    
  }


  shouldComponentUpdate(nextProps, nextState) {

    if (this.props.notCollapse && this.props.data.result !== nextProps.data.result) {
      this.orderByNodes(nextProps.data.result.nodes);
      return false;
    }

    if (this.props.choosen !== nextProps.choosen) {
      this.highlightChoosen(nextProps.choosen);
      return false;
    }

    for(const idx in nextProps) {
      if (nextProps[idx] !== this.props[idx]) {
        console.log('props changed--------', idx, nextProps[idx], this.props[idx]);
      }
    }

    for(const idx in nextState) {
      if (nextState[idx] !== this.state[idx]) {
        console.log('state changed--------', idx, nextState[idx], this.state[idx]);
      }
    }

    return false;
  }

  componentDidUpdate = () => {
    this.drawMatrix()
    if (this.setNodes)
      this.setNodes(this.totalNodes)
  }

  render() {
    return (
      <div ref={this.matrixRef} className="matrixview" id="matrix-view" />
    )
  }
}

